/**
 * Shell strip for everything the app has to say about the network and the
 * writes waiting on it.
 *
 * A strip, not a toast: it stays as long as the condition does. One condition
 * shows at a time, in the order below — a browser that can never sync is worse
 * news than being offline, which is worse news than a queue that simply has
 * not drained yet. Every count is a button rather than a label: the sheet
 * behind it is the only place a change can be retried or discarded, so there
 * has to be a visible way in.
 */

import React, { useState } from 'react';

import { useOfflineStatus } from '@shared/hooks/useOfflineStatus';

import { PendingChangesSheet } from './PendingChangesSheet';

/** "1 change" / "2 changes". */
const changes = (count: number): string => `${count} change${count === 1 ? '' : 's'}`;

const LINK_CLASS =
  'text-foreground rounded-sm underline underline-offset-2 hover:no-underline focus-visible:outline-2';

interface OpenSheetProps {
  onOpen: () => void;
}

/** A count (or "See changes") the user can press to see what is behind it. */
const SheetButton: React.FC<OpenSheetProps & { children: React.ReactNode }> = ({
  onOpen,
  children,
}) => (
  <button type="button" onClick={onOpen} className={LINK_CLASS}>
    {children}
  </button>
);

/** Web Locks is how replay is serialised across tabs; without it nothing drains. */
const LocksUnavailable: React.FC<OpenSheetProps> = ({ onOpen }) => (
  <>
    <span className="font-medium">
      Changes are saved on this device but this browser can&apos;t sync them.
    </span>
    <span className="text-muted-foreground">Open the app in Safari 15.4 or newer, or Chrome.</span>
    <SheetButton onOpen={onOpen}>See changes</SheetButton>
  </>
);

/** No network: say what still works, and how much is stacked up behind it. */
const Offline: React.FC<OpenSheetProps & { pendingCount: number }> = ({ onOpen, pendingCount }) => (
  <>
    <span className="font-medium">You&apos;re offline.</span>
    {pendingCount > 0 ? (
      <SheetButton onOpen={onOpen}>{`${changes(pendingCount)} waiting to sync`}</SheetButton>
    ) : (
      <span className="text-muted-foreground">What you have opened is still here.</span>
    )}
  </>
);

/** A replay hit a 401: the work is safe, the session is not. */
const NeedsSignIn: React.FC<OpenSheetProps & { pendingCount: number }> = ({
  onOpen,
  pendingCount,
}) => (
  <>
    <span className="font-medium">
      {`Sign in to sync your ${pendingCount} pending change${pendingCount === 1 ? '' : 's'}.`}
    </span>
    <SheetButton onOpen={onOpen}>See changes</SheetButton>
  </>
);

/** The server refused something; it waits for a Retry or a Discard. */
const Failed: React.FC<OpenSheetProps & { failedCount: number }> = ({ onOpen, failedCount }) => (
  <>
    <span className="font-medium">{`${changes(failedCount)} couldn't sync.`}</span>
    <SheetButton onOpen={onOpen}>See changes</SheetButton>
  </>
);

interface BackOnlineProps extends OpenSheetProps {
  pendingCount: number;
  syncing: boolean;
  syncNow: () => void;
}

/** Connected again with work still queued: offer to start it now. */
const BackOnline: React.FC<BackOnlineProps> = ({ onOpen, pendingCount, syncing, syncNow }) => (
  <>
    <span className="font-medium">Back online.</span>
    <SheetButton onOpen={onOpen}>{`${pendingCount} waiting`}</SheetButton>
    <button
      type="button"
      onClick={syncNow}
      disabled={syncing}
      className="bg-background hover:bg-background/80 text-foreground ml-auto rounded-md px-2 py-0.5 text-xs transition-colors disabled:opacity-50"
    >
      {syncing ? 'Syncing…' : 'Sync now'}
    </button>
  </>
);

/** The one place the app talks about the network, mounted once in the shell. */
export const OfflineBanner: React.FC = () => {
  const { online, pendingCount, failedCount, needsSignIn, locksUnavailable, syncing, syncNow } =
    useOfflineStatus();
  const [sheetOpen, setSheetOpen] = useState(false);
  const onOpen = (): void => setSheetOpen(true);

  const content = ((): React.ReactNode => {
    if (locksUnavailable) return <LocksUnavailable onOpen={onOpen} />;
    if (!online) return <Offline onOpen={onOpen} pendingCount={pendingCount} />;
    if (needsSignIn) return <NeedsSignIn onOpen={onOpen} pendingCount={pendingCount} />;
    if (failedCount > 0) return <Failed onOpen={onOpen} failedCount={failedCount} />;
    if (pendingCount > 0) {
      return (
        <BackOnline
          onOpen={onOpen}
          pendingCount={pendingCount}
          syncing={syncing}
          syncNow={syncNow}
        />
      );
    }
    return null;
  })();

  // The sheet outlives the strip that opened it: a replay finishing while the
  // user reads the list clears the banner's condition, and unmounting the
  // sheet with it would shut the list in their face.
  if (!content && !sheetOpen) return null;
  return (
    <>
      {content && (
        <div
          role="status"
          className="bg-muted text-foreground border-border flex items-center gap-2 border-b px-3 py-1.5 text-sm"
        >
          {content}
        </div>
      )}
      <PendingChangesSheet isOpen={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
};
